import { create } from "zustand";

/**
 * Front-end side of the AI credit quota gate.
 *
 * When an org runs out of AI credits the backend raises `CreditQuotaExceeded`,
 * which serialises to a **top-level** body (`{error: "credit_quota_exceeded",
 * ...}`) — deliberately NOT wrapped in `detail`. Nothing in the HTTP client
 * recognised that shape, so it fell through to the generic branch and the user
 * was shown `JSON.stringify(rawBody)`: a raw JSON blob at the single most
 * conversion-critical moment in the product.
 *
 * This mirrors the 423 scope-block store exactly: the client narrows the body,
 * pushes it here, and <CreditBlockModal> renders it as an actionable upsell
 * instead of an error.
 */

export interface CreditBlockPayload {
  error: "credit_quota_exceeded";
  reason: string;
  message: string;
  credits_remaining: number;
  credits_required: number;
}

/**
 * Narrow an arbitrary response body to a CreditBlockPayload.
 *
 * Reads the TOP-LEVEL body, not `detail` — `CreditQuotaExceeded.to_payload()`
 * is returned unwrapped. Both numeric fields are validated because the modal
 * renders them; a malformed-but-shaped body must not drive the UI with
 * `undefined`.
 */
export function isCreditBlockPayload(d: unknown): d is CreditBlockPayload {
  if (!d || typeof d !== "object") return false;
  const p = d as Partial<CreditBlockPayload>;
  return (
    p.error === "credit_quota_exceeded" &&
    typeof p.message === "string" &&
    typeof p.credits_remaining === "number" &&
    typeof p.credits_required === "number"
  );
}

interface CreditBlockState {
  /** The active credit block, or null when nothing is blocked. */
  block: CreditBlockPayload | null;
  /** Bumped on every show() so the modal can reset per block. */
  seq: number;
  /** Surface a credit block — opens <CreditBlockModal>. */
  show: (block: CreditBlockPayload) => void;
  /** Dismiss the modal. */
  dismiss: () => void;
}

export const useCreditBlockStore = create<CreditBlockState>((set) => ({
  block: null,
  seq: 0,
  show: (block) => set((s) => ({ block, seq: s.seq + 1 })),
  dismiss: () => set({ block: null }),
}));
